import { Fragment, useEffect } from 'react';

import { Transition } from '@headlessui/react';
import { XCircleIcon } from '@heroicons/react/20/solid';
import {
  CheckCircleIcon,
  InformationCircleIcon,
} from '@heroicons/react/24/outline';
import { useDispatch, useSelector } from 'react-redux';

import { AppState } from '../lib/store';
import { closeNotification } from '../slice/notification.slice';

const Notification = () => {
  const dispatch = useDispatch();
  const { isOpen, title, type, description, duration } = useSelector(
    (state: AppState) => state.notification
  );

  useEffect(() => {
    if (isOpen) {
      const timer = setTimeout(() => dispatch(closeNotification()), duration);
      return () => {
        clearTimeout(timer);
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  return (
    <>
      <div
        aria-live="assertive"
        className="pointer-events-none fixed inset-0 z-50 flex items-end px-4 py-6 sm:items-start sm:p-6"
      >
        <div className="flex w-full flex-col items-end space-y-4 sm:items-end">
          <Transition
            show={isOpen}
            as={Fragment}
            enter="transform ease-out duration-300 transition"
            enterFrom="translate-y-2 opacity-0 sm:translate-y-0 sm:translate-x-2"
            enterTo="translate-y-0 opacity-100 sm:translate-x-0"
            leave="transition ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="pointer-events-auto w-full max-w-sm overflow-hidden rounded-lg bg-white shadow-md ring-1 ring-black/5">
              <div className="p-4">
                <div className="flex items-start">
                  <div className="shrink-0">
                    <NotificationIcon type={type} />
                  </div>
                  <div className="ml-3 w-0 flex-1 pt-0.5">
                    <p className="text-sm font-medium text-gray-900">{title}</p>
                    <p className="mt-1 text-sm text-gray-500">{description}</p>
                  </div>
                  <div className="ml-4 flex shrink-0">
                    <button
                      className="inline-flex rounded-md bg-white text-gray-400 hover:text-gray-500 "
                      onClick={() => dispatch(closeNotification())}
                    >
                      <span className="sr-only">Close</span>
                      <XCircleIcon className="h-5 w-5" aria-hidden="true" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </Transition>
        </div>
      </div>
    </>
  );
};

const NotificationIcon = ({ type }: { type: 'success' | 'info' | 'error' }) => {
  return (
    <>
      {type === 'info' && (
        <InformationCircleIcon
          className="h-6 w-6 text-green-400"
          aria-hidden="true"
        />
      )}
      {type === 'success' && (
        <CheckCircleIcon
          className="h-6 w-6 text-green-400"
          aria-hidden="true"
        />
      )}
      {type === 'error' && (
        <XCircleIcon className="h-6 w-6 text-red-400" aria-hidden="true" />
      )}
    </>
  );
};

export default Notification;
